import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const KineticText = ({ lines, className = "", lineClassName = "", delay = 0.25, as: Tag = "h1" }) => (
  <Tag className={className} data-testid="kinetic-headline">
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: delay + i * 0.13, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const Reveal = ({ children, delay = 0, y = 30, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const Stagger = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09 } } }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "" }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y: 26 },
      show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
    }}
  >
    {children}
  </motion.div>
);

export const ParallaxImage = ({ src, alt = "", className = "", speed = 14, position = "center" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed}%`, `${speed}%`]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, scale: 1.2, objectPosition: position }}
        className="w-full h-full object-cover will-change-transform"
      />
    </div>
  );
};

export const Marquee = ({ items, dark = false }) => {
  const row = [...items, ...items];
  return (
    <div
      data-testid="editorial-marquee"
      className={`relative overflow-hidden py-5 border-y ${dark ? "border-cream/10 bg-forestdeep text-cream/80" : "border-forest/10 bg-creamdark/60 text-forest/70"}`}
    >
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {row.map((item, i) => (
          <span key={i} className="flex items-center font-serif italic text-lg sm:text-xl px-6">
            {item}
            <span className="ml-12 text-gold not-italic text-sm">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};
